<template>
    <Dialog :open="open" :title="$t('alerts.alertDetails')" max-width="900" @close="close">
        <v-container class="pa-0 pb-2">
            <v-form ref="form">
                <v-row>
                    <v-col class="pb-0" cols="6">
                        <v-text-field :label="$t('alerts.sender')" :value="alert.FullName" disabled readonly/>
                    </v-col>
                    <v-col class="pb-0" cols="6">
                        <v-text-field v-if="location" :label="$t('user.location')" :value="location" disabled readonly/>
                        <div v-else class="primary--text text-center mt-2">{{ $t('alerts.noLocationData') }}</div>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="6">
                        <v-text-field :label="$t('date')" :value="formatDateTime(alert.CreatedAt)" disabled readonly/>
                        <v-text-field :label="$t('notifications.severity')" :value="alert.Severity" disabled readonly/>
                        <v-text-field :label="$t('notifications.title')" :value="alert.Title" disabled readonly/>
                        <v-text-field :label="$t('alerts.alertType')" :value="alert.Type" disabled hide-details
                                      readonly/>
                    </v-col>
                    <v-col cols="6">
                        <MapBasic v-if="location" :markers="markers" before-leave click-full-screen/>
                    </v-col>
                </v-row>
                <v-row class="mb-4">
                    <v-col cols="6">
                        <v-textarea :label="$t('alerts.details')" :value="alert.Text" class="mb-8" disabled hide-details
                                    readonly
                                    rows="3"/>
                        <v-textarea :label="$t('authentication.address')" :value="alert.Address" disabled hide-details
                                    readonly
                                    rows="3"/>
                    </v-col>
                    <v-col cols="6">
                        <div v-if="alert.PayloadUrl" class="mb-2">
                            {{ $t('alerts.media') }}
                        </div>
                        <audio v-if="isAudioPayload && open" class="audio-player mt-2" controls>
                            <source
                                    :src="alert.PayloadUrl">
                            {{ $t('warnings.notSupportAudio') }}
                        </audio>
                        <video v-if="isVideoPayload && open" class="video-player" controls width="100%">
                            <source :src="alert.PayloadUrl"/>
                        </video>
                        <a v-if="isImagePayload" :href="alert.PayloadUrl" target="_blank">
                            <img :src="alert.PayloadUrl"
                                 alt="payload"
                                 class="image-payload"/></a>
                    </v-col>
                </v-row>
                <div class="mt-4 pl-1 primary--text ">
                    {{ $t('notifications.sentTo') }}
                </div>
                <v-data-table
                        :headers="header"
                        :items="filteredRecipients"
                        :loading="loading"
                        class="rounded-lg shadow mt-2">
                    <template v-slot:[`item.AvatarUrl`]="{item}">
                        <v-avatar size="35">
                            <img
                                    v-if="item.AvatarUrl"
                                    :src="item.AvatarUrl"
                                    alt="avatar">
                            <v-icon v-else size="50">
                                mdi-account-circle
                            </v-icon>
                        </v-avatar>
                    </template>
                    <template v-slot:[`header.Channel`]>
                        <FilterDropDown
                                :key="alertID + 'channel'"
                                v-model="filters.channels"
                                :available-values="Array.from(availableValues.channels)"
                                :filter-values="channels"
                                :name="$t('notifications.channel')"/>
                    </template>
                    <template v-slot:[`header.Status`]>
                        <FilterDropDown
                                :key="alertID + 'status'"
                                v-model="filters.status"
                                :available-values="Array.from(availableValues.status)"
                                :filter-values="statuses"
                                :name="$t('status')"/>
                    </template>
                    <template v-slot:[`header.CircleName`]>
                        <FilterDropDown
                                :key="alertID + 'circle'"
                                v-model="filters.circles"
                                :filter-values="circles"
                                :name="$t('menu.circles')"/>
                    </template>
                </v-data-table>
            </v-form>
        </v-container>
    </Dialog>
</template>

<script>
import Dialog from "@/components/reusable/Dialog/Dialog";
import {formatDateTime} from "@/helper/helper";
import {
  CHANNEL_EMAIL,
  CHANNEL_REALTIME,
  CHANNEL_SMS,
  NOTIFICATION_COMPLETED,
  NOTIFICATION_FAILED,
  NOTIFICATION_PENDING
} from "@/helper/constants";
import FilterDropDown from "@/components/reusable/FilterDropDown/FilterDropDown";


export default {
  name: "AlertDetails",
  components: {
    FilterDropDown,
    Dialog,
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    alertID: {
      type: Number,
      required: false,
    },
    alert: {
      type: Object,
      required: true,
    },
    userId: {
      type: Number,
      required: false,
    },
  },
  computed: {
    isVideoPayload() {
      if (!this.alert.PayloadUrl) {
        return false;
      }
      return ['.mp4', '.avi', '.mov', '.wmv', '.vwm'].includes(this.alert.PayloadUrl.substr(this.alert.PayloadUrl.lastIndexOf('.')));
    },
    isAudioPayload() {
      if (!this.alert.PayloadUrl) {
        return false;
      }
      return ['.mp3', '.wav'].includes(this.alert.PayloadUrl.substr(this.alert.PayloadUrl.lastIndexOf('.')));
    },
    isImagePayload() {
      if (!this.alert.PayloadUrl) {
        return false;
      }
      return ['.jpg', '.jpeg', '.bmp', '.png'].includes(this.alert.PayloadUrl.substr(this.alert.PayloadUrl.lastIndexOf('.')));
    },
    markers() {
      return this.alert.Location ? [{lat: this.alert.Location.Latitude, lng: this.alert.Location.Longitude}] : [];
    },
    location() {
      return this.alert.Location ? this.alert.Location.Latitude + ', ' + this.alert.Location.Longitude : '';
    },
    filteredRecipients() {
      return this.recipients.filter((recipient) => this.filters.channels.includes(recipient.Channel) && this.filters.status.includes(recipient.Status));
    },
    header() {
      return [
        {text: this.$t('circles.avatar'), value: 'AvatarUrl'},
        {text: this.$t('authentication.firstName'), value: 'FirstName'},
        {text: this.$t('authentication.lastName'), value: 'LastName'},
        {text: this.$t('authentication.email'), value: 'Email'},
        {text: this.$t('notifications.channel'), value: 'Channel'},
        {text: this.$t('status'), value: 'Status'},
      ];
    },
    channels() {
      return [
        {
          value: CHANNEL_SMS,
          text: this.$t('settings.SMS')
        },
        {
          value: CHANNEL_REALTIME,
          text: this.$t('settings.Realtime')
        },
        {
          value: CHANNEL_EMAIL,
          text: this.$t('settings.Email')
        },
      ];
    },
    statuses() {
      return [
        {
          value: NOTIFICATION_PENDING,
          text: this.$t('notifications.pending')
        },
        {
          value: NOTIFICATION_COMPLETED,
          text: this.$t('notifications.completed')
        },
        {
          value: NOTIFICATION_FAILED,
          text: this.$t('notifications.failed')
        },
      ];
    },
  },
  watch: {
    alertID(id) {
      if (id) {
        this.getAlertDetails(id);
      }
    },
  },
  mounted() {
    if (this.alertID) {
      this.getAlertDetails(this.alertID);
    }
  },
  data() {
    return {
      recipients: [],
      loading: false,
      filters: {
        channels: [],
        status: [],
        circles: [],
      },
      availableValues: {
        channels: new Set(),
        status: new Set(),
        circles: new Set(),
      },
      circles: [],
    };
  },
  methods: {
    formatDateTime,
    getAlertDetails(id) {
      const url = this.$store.getters.alertRecipients(this.userId, id);
      this.loading = true;
      this.recipients = [];
      this.axios.get(url).then(({data}) => {
        const recipients = data.Result;
        recipients.forEach(recipent => {
          recipent.Channels.forEach(channel => {
            this.recipients.push({...recipent, Channel: channel.Channel, Status: channel.Status})
          })
          if (!recipent.Channels || recipent.Channels.length === 0) {
            this.recipients.push({...recipent, Channel: '', Status: ''})
          }
        })
        this.availableValues.channels.clear();
        this.availableValues.status.clear();
        this.recipients.forEach(recipient => {
          this.availableValues.channels.add(recipient.Channel);
          this.availableValues.status.add(recipient.Status);
          if (!this.availableValues.circles.has(recipient.CircleName)) {
            this.circles.push({value: recipient.CircleName, text: recipient.CircleName});
          }
          this.availableValues.circles.add(recipient.CircleName);
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    close() {
      setTimeout(() => {
        this.$emit('close')
      }, 200)
    }
  }
};
</script>

<style lang="scss" scoped>
@import "alert-details";
</style>